import { takeUntil } from 'rxjs';
import { Texture } from 'pixi.js';
import { ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSidenav } from '@angular/material/sidenav';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { TokenizerComponent } from './tokenizer/tokenizer.component';
import { WatchAwareComponent } from './watch-aware.component';
import { Room } from 'src/sync/room';
import { Gfx } from 'src/graphics/gfx';
import { Account } from 'src/sync/account';
import { Settings } from 'src/graphics/settings';
import { Mouse } from 'src/graphics/mouse';
import { View } from 'src/graphics/view';
import { Store } from 'src/sync/store';
import { IToken, L } from 'src/sync/_api';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ChatComponent } from './chat/chat.component';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent extends WatchAwareComponent {
    @ViewChild('rightdrawer') rightdrawer!: MatSidenav;
    @ViewChild('chat') chat!: ChatComponent;
    public counter: number = 0;
    public tokenizer: MatDialogRef<TokenizerComponent> | undefined;
    public draggedAssetId?: string | null;
    public isMobile = false;
    public show: 'user' | 'chat' = 'user';
    public unreadMessages = 0;

    // TODO: Extract all token drop tech to a tokenzone attribute, refactor w/ control-tokens component
    constructor(
        public dialogs: MatDialog,
        public bottom: MatBottomSheet,
        public snackbar: MatSnackBar,
        public override changeDetectorRef: ChangeDetectorRef,
    ) {
        super(changeDetectorRef);

        // NB: Clear any token selections or similar
        window.onbeforeunload = function () {
            Mouse.cancel();
            // return "Your work will be lost.";
        };

        Store.isMobile.pipe(takeUntil(this.destroyed)).subscribe((isMobile) => {
            this.isMobile = isMobile;
            const switchingMobileToDesktop = this.isMobile == false && this.show == 'chat' && this.rightdrawer
            if (switchingMobileToDesktop) {
                this.rightdrawer.close();
            }
        });
        const body = document.querySelector('body') as HTMLElement;

        const doNothing = (e: DragEvent) => { e.stopPropagation(); e.preventDefault(); };
        body.addEventListener('dragstart', (e) => {
            const element = e?.target as HTMLElement;
            const isToken = element?.classList.contains('idle-token');
            if (isToken) {
                this.draggedAssetId = element.getAttribute('tokenid');
            }
        });

        body.addEventListener('drop', async (e) => {
            e.stopPropagation();
            e.preventDefault();
            Mouse.updatePosition(e.pageX, e.pageY);

            if (this.draggedAssetId) {
                if ((e.target as HTMLElement).nodeName != 'CANVAS') return;
                const point = View.viewport.toWorld({ x: e.clientX, y: e.clientY });
                const tokenRadius = Settings.gridOffset / 2;
                const x = Mouse.snap(point.x - tokenRadius) + tokenRadius;
                const y = Mouse.snap(point.y - tokenRadius) + tokenRadius;
                const token: IToken = {
                    id: crypto.randomUUID(),
                    assetId: this.draggedAssetId,
                    rotation: 0,
                    scale: { x: 1, y: 1 },
                    position: { x, y },
                    label: '',
                    visible: true,
                    locked: false
                };
                Room.document.transact(() => Room.tokens.set(token.id, token), L.Tokens);
                this.draggedAssetId = null;
            } else if (e.dataTransfer) {
                await this.transferImageData(e.dataTransfer);
            } else if (e.target) {
                // TODO: Can images from chat be allowed?
                /*const target = e.target as HTMLElement;
                if (target.tagName == 'IMG') {
                    Store.imageUpload = e.target as HTMLImageElement;
                    Store.imageUploadUrl = Store.imageUpload.src;
                    await this.uploadImage(Store.imageUpload.src);
                }*/
            }
        });
        body.addEventListener('dragenter', doNothing);
        body.addEventListener('dragover', doNothing);
        body.addEventListener('dragleave', doNothing);
        body.addEventListener('paste', async (e) => {
            if (e.clipboardData == null) return;
            await this.transferImageData(e.clipboardData);
        });

        window.addEventListener('popstate', (event) => {
            if (this.isMobile == false) return;
            if (event.state.url == '/chat') {
                this.show = 'chat';
                this.rightdrawer.toggle();
            } else {
                this.rightdrawer.close();
            }
        });
    }

    public ngOnInit() {
        Store.fullAccess.subscribe((fullAccess) => {
            const fogAlpha = fullAccess ? Gfx.layers[L.Fog].cfg.alpha : 1; // opaque fog for locked down players
            Gfx.containers.layers[L.Fog].alpha = fogAlpha;
            this.changeDetectorRef.detectChanges();
        });
    }

    public logout() {
        localStorage.removeItem('login-token');
        const url = new URL(window.location.href);
        history.replaceState({}, '', url);
        window.location.reload();
    }

    public open(mode: 'chat' | 'user') {
        const changingMode = this.show != mode;

        if (changingMode && this.rightdrawer.opened) {
            this.rightdrawer.close().then(() => {
                this.show = mode;
                this.rightdrawer.open();
            });
        } else {
            this.show = mode;
            this.rightdrawer.toggle();
        }

        if (this.isMobile && this.show == 'chat' && this.rightdrawer.opened) {
            this.unreadMessages = 0;
            this.chat.scrollChatbox(true);
        }
    }

    public closeDrawer() {
        setTimeout(() => this.rightdrawer.close());
    }

    public uploadFile() {

    }

    public gotoPatreon() {
        window.open('https://www.patreon.com/ransackgames', '_blank');
    }

    public gotoDiscord() {
        window.open('https://discord.gg/cjkuXDakFP', '_blank');
    }

    public fitViewExtents() {
        this.snackbar.open(`Aligned your view to the revealed area.`, undefined, { duration: 1500 });
        View.fitExtents();
    }

    public openDrawer() {
        // Kb.on({ code: 'KeyT' }, () => {
        //     const drawerRef = bottomSheet.open(SocialShareComponent, {
        //         ariaLabel: 'Share on social media'
        //     });
        // });
    }
    public async transferImageData(data: DataTransfer) {
        const file = data.files[0];
        if (file == null) return;
        const imageTransfer = new Image();
        const url = URL.createObjectURL(file);
        imageTransfer.onload = async (e: Event) => {
            if (url == Store.imageUploadUrl) return;
            if (this.tokenizer) return;

            Store.imageUploadUrl = url;
            Store.imageUpload = e.target as HTMLImageElement;
            await this.insertOrTokenizeImage(file);
        }
        imageTransfer.src = url;
    }

    public async insertOrTokenizeImage(file: Blob) {
        if (Store.connection.getValue() == false) {
            this.snackbar.open('Cannot upload files while disconnected.', 'Dismiss', { duration: 3000 });
            return;
        }

        if (Gfx.activeLayer.id == L.Uploads) {
            const filename = await Account.uploadImage(file);
            const texture = Texture.from(Store.imageUpload);
            const centerX = texture.width / 2;
            const centerY = texture.height / 2;
            const x = Mouse.snap(Gfx.mouse.x - centerX);
            const y = Mouse.snap(Gfx.mouse.y - centerY);
            await Room.addImage(filename, x, y);
        } else {
            const myTokenCount = Room.tokenAssets.asArray().filter(x => x.uploaderId == Account.id).length;
            if (Store.isReferee.value == false && myTokenCount >= 20) {
                this.snackbar.open('Maximum token uploads is 20.', 'Dismiss', { duration: 2000 });
                return;
            }

            this.tokenizer = this.dialogs.open(TokenizerComponent);
            const closeEvent = this.tokenizer.afterClosed();
            closeEvent.subscribe(() => this.tokenizer = undefined);
        }
    }

    public updateUnread(value: number) {
        const isChatOpen = this.isMobile && this.show == 'chat' && this.rightdrawer.opened;
        this.unreadMessages = isChatOpen ? 0 : value;
    }
}
